import { Geolocation } from '@capacitor/geolocation';
import { ForegroundService } from '@capawesome-team/capacitor-android-foreground-service';
import { isNative } from './platform';

const GPS_INTERVAL = 5_000; // 5 seconds for native

let watchId: string | null = null;
let intervalId: ReturnType<typeof setInterval> | null = null;

interface GpsCallback {
  (point: { lat: number; lng: number; accuracy: number; timestamp: number }): void;
}

/** Request all location permissions needed for background tracking. */
export async function requestLocationPermissions(): Promise<boolean> {
  if (!isNative()) return true;

  try {
    // Request fine location first
    let status = await Geolocation.requestPermissions({ permissions: ['location'] });
    if (status.location !== 'granted') return false;

    // Request coarse location
    status = await Geolocation.requestPermissions({ permissions: ['coarseLocation'] });

    return true;
  } catch {
    return false;
  }
}

/** Start the foreground service with a persistent notification. */
async function startForegroundService(): Promise<void> {
  try {
    await ForegroundService.startForegroundService({
      id: 1001,
      title: 'Swarnim Track',
      body: 'Tracking your trip...',
      smallIcon: 'ic_stat_directions_car',
    });
  } catch (err) {
    console.error('Failed to start foreground service:', err);
  }
}

/** Stop the foreground service. */
async function stopForegroundService(): Promise<void> {
  try {
    await ForegroundService.stopForegroundService();
  } catch {
    // Service might not be running
  }
}

/**
 * Start native GPS tracking with foreground service.
 * Calls onPoint every ~5 seconds with GPS data.
 */
export async function startNativeGps(onPoint: GpsCallback): Promise<void> {
  if (!isNative()) return;

  const hasPermission = await requestLocationPermissions();
  if (!hasPermission) {
    console.error('Location permission denied');
    return;
  }

  // Start foreground service so Android doesn't kill us
  await startForegroundService();

  // Use watchPosition for continuous tracking
  watchId = await Geolocation.watchPosition(
    {
      enableHighAccuracy: true,
      timeout: 10_000,
      maximumAge: 0,
    },
    (position, err) => {
      if (err || !position) return;
      onPoint({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      });
    },
  );

  // Also poll at fixed intervals as a fallback (some devices throttle watchPosition)
  intervalId = setInterval(async () => {
    try {
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 8_000,
        maximumAge: 3_000,
      });
      onPoint({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        timestamp: pos.timestamp,
      });
    } catch {
      // Silently retry next interval
    }
  }, GPS_INTERVAL);
}

/** Stop native GPS tracking and foreground service. */
export async function stopNativeGps(): Promise<void> {
  if (watchId !== null) {
    await Geolocation.clearWatch({ id: watchId });
    watchId = null;
  }

  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }

  await stopForegroundService();
}
